/**
 * Stale-chunk recovery.
 *
 * Every deploy renames the hashed asset files. A tab that loaded index.html
 * before the deploy still points its lazy imports at the old names, so the
 * next `import()` fails with "Failed to fetch dynamically imported module"
 * and the page-level ErrorBoundary unmounts the whole app. Vite reports
 * that failure as a `vite:preloadError` event; calling preventDefault and
 * reloading once picks up the current build.
 *
 * The reload is guarded per page URL through sessionStorage so a chunk
 * that is genuinely missing from the current build cannot loop the tab.
 */

const RELOAD_GUARD_KEY = 'agion.preloadErrorReload'

export interface PreloadErrorReloadDeps {
  addEventListener: (type: string, listener: (event: Event) => void) => void
  reload: () => void
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  currentUrl: () => string
}

function readStorage(storage: PreloadErrorReloadDeps['storage'], key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage(storage: PreloadErrorReloadDeps['storage'], key: string, value: string | null): void {
  try {
    if (value === null) storage?.removeItem(key)
    else storage?.setItem(key, value)
  } catch {
    // Storage may be unavailable (private mode, quota); reload still runs.
  }
}

/**
 * Handle one `vite:preloadError`. Returns true when a reload was issued,
 * false when the guard suppressed it so the error surfaces normally.
 */
export function handlePreloadError(event: Event, deps: PreloadErrorReloadDeps): boolean {
  const url = deps.currentUrl()
  if (readStorage(deps.storage, RELOAD_GUARD_KEY) === url) {
    writeStorage(deps.storage, RELOAD_GUARD_KEY, null)
    return false
  }
  event.preventDefault()
  writeStorage(deps.storage, RELOAD_GUARD_KEY, url)
  deps.reload()
  return true
}

/** Clear the guard once a page has loaded its lazy chunks successfully. */
export function clearPreloadErrorGuard(storage: PreloadErrorReloadDeps['storage']): void {
  writeStorage(storage, RELOAD_GUARD_KEY, null)
}

export function installPreloadErrorReload(deps: PreloadErrorReloadDeps): void {
  deps.addEventListener('vite:preloadError', (event) => {
    handlePreloadError(event, deps)
  })
}

export function installPreloadErrorReloadOnWindow(): void {
  if (typeof window === 'undefined') return
  let storage: PreloadErrorReloadDeps['storage'] = null
  try {
    storage = window.sessionStorage
  } catch {
    storage = null
  }
  installPreloadErrorReload({
    addEventListener: (type, listener) => window.addEventListener(type, listener),
    reload: () => window.location.reload(),
    storage,
    currentUrl: () => window.location.href,
  })
}
